import { reverseGeocode } from "./googleMaps";

const coordinatePair = /^\s*\(?\s*(?:lat(?:itude)?\s*[:=]\s*)?([+-]?\d+(?:\.\d+)?)\s*,\s*(?:(?:lng|lon|longitude)\s*[:=]\s*)?([+-]?\d+(?:\.\d+)?)\s*\)?\s*$/i;

const getCoordinates = (value) => {
  if (typeof value === "string") {
    const match = value.match(coordinatePair);
    return match ? [Number(match[1]), Number(match[2])] : null;
  }
  if (value && typeof value === "object") {
    const lat = value.latitude ?? value.lat;
    const lng = value.longitude ?? value.lng ?? value.lon;
    if (lat == null || lng == null || lat === "" || lng === "") return null;
    return [Number(lat), Number(lng)];
  }
  return null;
};

export const resolveReportLocation = async (value, cache) => {
  const name = typeof value === "object" && value
    ? value.location_name || value.name
    : null;
  if (name) return String(name);

  const coordinates = getCoordinates(value);
  if (!coordinates) return typeof value === "string" ? value.trim() || "-" : "-";

  const [latitude, longitude] = coordinates;
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude) ||
      Math.abs(latitude) > 90 || Math.abs(longitude) > 180) {
    return "Location unavailable";
  }

  const key = `${latitude},${longitude}`;
  if (!cache.has(key)) {
    // Cache in-flight lookups too: repeated punches at the same place share a request.
    cache.set(key, reverseGeocode(latitude, longitude)
      .then((address) => {
        const label = address?.shortAddress || address?.fullAddress;
        if (!label) cache.delete(key);
        return label || "Location unavailable";
      })
      .catch(() => {
        cache.delete(key);
        return "Location unavailable";
      }));
  }
  return cache.get(key);
};

export const resolveReportLocations = async (rows, cache) => {
  let nextIndex = 0;
  const resolved = new Array(rows.length);
  // Bound lookup traffic when a report contains many different locations.
  await Promise.all(Array.from({ length: Math.min(4, rows.length) }, async () => {
    while (nextIndex < rows.length) {
      const index = nextIndex++;
      const row = rows[index];
      const inLocation = await resolveReportLocation(row.in_location, cache);
      const outLocation = await resolveReportLocation(row.out_location, cache);
      resolved[index] = { ...row, in_location: inLocation, out_location: outLocation };
    }
  }));
  return resolved;
};
